import React, { useState } from 'react';
import { useTheme } from '@/context/ThemeContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditTorDialog({ open, onClose, tor, onSuccess }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [name, setName] = useState(tor?.name || '');
  const [email, setEmail] = useState(tor?.email || '');
  const [phone, setPhone] = useState(tor?.phone || '');
  const [amount, setAmount] = useState(tor?.amount?.toString() || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/tors/${tor.tor_id}`, {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        amount: parseFloat(amount) || tor.amount,
      }, { withCredentials: true });
      toast.success('Updated successfully');
      onClose();
      onSuccess();
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className={`rounded-md max-w-md ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`} data-testid="edit-tor-dialog">
        <DialogHeader>
          <DialogTitle className={`font-outfit text-lg font-semibold ${isDark ? 'text-zinc-50' : 'text-zinc-950'}`}>
            Edit {tor?.tor_type === 'debtor' ? 'Debtor' : 'Creditor'}
          </DialogTitle>
          <DialogDescription className={`font-ibm text-xs ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
            Update the details below
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Name</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="edit-tor-name"
            />
          </div>
          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Email</label>
            <Input
              value={email}
              onChange={e => setEmail(e.target.value)}
              className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="edit-tor-email"
            />
          </div>
          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Phone</label>
            <Input
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="edit-tor-phone"
            />
          </div>
          <div>
            <label className={`font-ibm text-[10px] font-semibold uppercase tracking-[0.2em] mb-1.5 block ${isDark ? 'text-zinc-500' : 'text-zinc-500'}`}>Amount</label>
            <Input
              value={amount}
              onChange={e => setAmount(e.target.value)}
              type="number"
              min="0"
              className={`h-10 rounded-sm font-ibm ${isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-50' : 'bg-zinc-50 border-zinc-200 text-zinc-950'}`}
              data-testid="edit-tor-amount"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={onClose}
            className={`flex-1 h-10 font-ibm text-sm font-medium rounded-sm border transition-all active:scale-[0.98] ${
              isDark ? 'border-zinc-700 text-zinc-400 hover:bg-zinc-800' : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
            }`}
            data-testid="cancel-edit-tor"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 h-10 bg-yellow-400 text-zinc-950 font-ibm text-sm font-semibold rounded-sm hover:bg-yellow-500 active:scale-[0.98] transition-all disabled:opacity-50"
            data-testid="submit-edit-tor"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
